import React, {useContext, useEffect, useState} from "react";
import moment from "moment";
import {Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  Bug01Icon, Cancel01Icon, CircleCheckIcon, CustomerSupportIcon, EyeIcon, HeadsetIcon, PhoneForwardedIcon, PhoneIcon,
  Refresh01Icon, UserEdit01Icon
} from "@hugeicons/core-free-icons";
import {useSetAtom} from "jotai";
import {toast} from "react-toastify";
import {ConversationRepository} from "../../../repositories/ConversationRepository";
import {SignInContext} from "../../../context/SignInContext";
import useConfirm from "../../../hooks/useConfirm";
import useCallConfirm from "../../../hooks/useCallConfirm";
import {useStompWebsocket} from "../../../hooks/useStompWebsocket";
import {FlowProtectionButton} from "../../../components/FlowProtectionButton";
import {useFlowProtection} from "../../../hooks/useFlowProtection";
import {SelectedConversationAudioPlayer, SelectedMessageAudioPlayer} from "../../project/supervision-page/atoms";
import {SupervisionConversationHistory} from "../../project/supervision-page/components/supervision-conversation-history";
import {ContactPhoneNumber} from "../../project/supervision-page/components/contact-phone-number";
import {AVATAR_COLORS, initialsOf, SenderName, StatusBadge, TagPills, TransferNumber} from "../dashboard/components/conversationUi";
import {subProjectLabel} from "../../../utils/subProjects";
import {ProjectRoles} from "../../../context/AppContext";
import {useRegistryProject} from "../RegistryProjectContext";
import {useWebrtc, WEBRTC_ROLES} from "../../../context/WebrtcContext";
import {SendToGroupButton} from "../../../components/webrtc/SendToGroupButton";

const roundButtonSx = {
  width: 34, height: 34, borderRadius: "50% !important", border: "1px solid #ECECF3", color: "#4A4A63", bgcolor: "#fff",
  "&:hover": {bgcolor: "#F4F4FA"},
};

const actionSx = (variant) => ({
  py: 1, px: 1.5, borderRadius: "8px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  transition: "background-color .15s ease",
  ...(variant === "accent"
    ? {color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}}
    : variant === "dark"
      ? {color: "#1E7F52", bgcolor: "#D9F2E4", "&:hover": {bgcolor: "#C9EBD8"}}
      : {color: "#374151", bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"}}),
});

// The selected conversation: header with caller, status and supervision actions, then its messages (and the
// caller's earlier conversations). flowOf(conversation): the Firebase flow it ran on (vdn manager, configuration,
// phonebook); fallbackFlow: used until it is known.
// conversationUpdate: last conversation pushed for the project, merged in when it is this one.
// onOpen(conversation): another conversation was started from here (chat), select it.
export const SupervisionDetail = ({
  conversationId, flowOf, fallbackFlow, subProjectOf, tagsOf, conversationUpdate, onOpen, onClose,
}) => {
  const {currentUser} = useContext(SignInContext);
  const {project, role, members} = useRegistryProject();
  const webrtc = useWebrtc();
  // project ADMIN / MANAGER / AGENT call through the browser phone only (it must be Connected), others by VDN
  const useBrowserPhone = WEBRTC_ROLES.includes(role);
  // the user's VDN from the project members (the flow's vdnManager is no longer updated)
  const memberVdn = members.find(member => member.username === currentUser?.username)?.vdn || null;
  const [conversation, setConversation] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const setAudioConversation = useSetAtom(SelectedConversationAudioPlayer);
  const setAudioMessage = useSetAtom(SelectedMessageAudioPlayer);
  const {token: protectionToken} = useFlowProtection();

  const flow = (conversation && flowOf(conversation)) || fallbackFlow;

  const [sessionUpdate] = useStompWebsocket(conversationId ? `/topic/session/${conversationId}` : null);

  const confirmMonitoring = useConfirm(
    `This conversation is already being monitored by ${conversation?.monitoredBy || "someone"}`,
    `Are you sure you want to take over monitoring from ${conversation?.monitoredBy || "someone"}?`
  );
  const confirmCall = useCallConfirm("Do you want to initiate conversation");

  useEffect(() => {
    setConversation(null);
  }, [conversationId]);

  // (re)load on open, whenever the session changes and once protected messages are unlocked (unmasked sender)
  useEffect(() => {
    if (!conversationId) return;
    ConversationRepository.getConversation({id: conversationId})
      .then(response => setConversation(response?.data || null))
      .catch(() => {
        toast.error("Conversation not found");
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, sessionUpdate, protectionToken]);

  // project-wide push for this conversation; the pushed sender is masked, keep ours
  useEffect(() => {
    if (!conversationUpdate?.id || String(conversationUpdate.id) !== String(conversationId)) return;
    setConversation(current => current
      ? {...current, ...conversationUpdate, sender: current.sender, senderName: current.senderName ?? conversationUpdate.senderName}
      : current);
  }, [conversationUpdate, conversationId]);

  useEffect(() => {
    try {
      setShowDebug(JSON.parse(localStorage.getItem("showDebug")) || false);
    } catch (e) {
      setShowDebug(false);
    }
  }, [conversationId]);

  const flowUid = conversation?.flowUid || flow?.id;
  const agentVdn = memberVdn || flow?.configuration?.redirect_vdn;
  const isVoice = conversation?.type !== "CHAT_BOT";
  const canHandover = project?.type !== "CHATBOT" && isVoice;
  // a user with their own VDN whose browser phone is not Connected for this project hands over / calls by VDN instead
  const callByVdnFallback = Boolean(memberVdn) && !webrtc.isConnected(project?.id);
  const callByBrowserPhone = useBrowserPhone && !callByVdnFallback;

  const monitorConversation = async () => {
    if (conversation?.monitoredBy && conversation.monitoredBy !== currentUser?.username) {
      if (!(await confirmMonitoring())) return;
    }
    ConversationRepository.monitorConversation({id: conversation?.id, username: currentUser?.username, flowUid})
      .then(response => {
        setConversation(current => response?.data || current);
        toast.success("You are now monitoring this conversation");
      })
      .catch(() => console.log("Failed to monitor conversation"));
  };

  // agents, managers and admins with their own VDN start supervising an active voice call as soon as they open it
  // (annotators only watch)
  useEffect(() => {
    const canMonitor = [ProjectRoles.AGENT, ProjectRoles.MANAGER, ProjectRoles.ADMIN].includes(role) && Boolean(memberVdn);
    if (conversation?.id && !conversation.monitoredBy && canMonitor && conversation.status === "ACTIVE"
      && conversation.type !== "CHAT_BOT") {
      monitorConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, flow, currentUser?.username, role]);

  const handoverToVdn = () => ConversationRepository.handoverToAgent({id: conversation?.id, username: currentUser?.username, vdn: agentVdn})
    .then(response => {
      setConversation(current => response?.data || current);
      toast.success("Successfully handed over to agent");
    })
    .catch(() => toast.error("Failed to hand over to agent"));

  // to this browser for browser phone roles (the conversation becomes HANDOVER, pushed over the websocket), to the
  // VDN otherwise
  const handleHandover = () => {
    if (!callByBrowserPhone) return handoverToVdn();
    return webrtc.takeOverCall({projectId: project?.id, conversationId: conversation?.id});
  };

  const toggleResolved = () => {
    ConversationRepository.toggleResolved({id: conversation?.id, flowUid})
      .then(response => setConversation(current => response?.data || current))
      .catch(error => console.log(error));
  };

  const callCustomer = async () => {
    if (callByBrowserPhone) {
      if (!(await confirmCall({title: `Do you want to call ${conversation?.sender || "the customer"} from your browser`}))) return;
      return webrtc.callCustomer({
        projectId: project?.id, conversationId: conversation?.id, phoneNumber: conversation?.sender,
      });
    }
    return callCustomerByVdn();
  };

  const callCustomerByVdn = async () => {
    if (!agentVdn || !conversation?.sender) {
      toast.info("No VDN configured for you in this project");
      return;
    }
    if (!(await confirmCall({title: `Do you want to initiate conversation with ${conversation.sender}`}))) return;
    const body = {
      username: currentUser?.username,
      vdn: agentVdn,
      phone_number: conversation.sender,
      initiate_call_api: flow?.configuration?.initiate_call_api,
    };
    ConversationRepository.initiateConversation({body, params: {conversation_id: conversation.id, flow_uid: flowUid}, flowUid})
      .then(response => toast.success(response?.data?.message || `Initiated conversation with ${body.phone_number}`))
      .catch(error => toast.error(error?.message || `Failed to initiate conversation with ${body.phone_number}`));
  };

  const close = () => {
    setAudioConversation(null);
    setAudioMessage(null);
    onClose();
  };

  if (!conversation) {
    return (
      <Stack sx={{height: "100%", minHeight: 0}}>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1.5} sx={{px: 3, pt: 2.5, pb: 2}}>
          <Stack direction={"row"} alignItems={"center"} gap={1.5}>
            <Skeleton variant={"circular"} width={44} height={44}/>
            <Box>
              <Skeleton variant={"rounded"} width={180} height={22} sx={{borderRadius: "8px"}}/>
              <Skeleton variant={"rounded"} width={240} height={14} sx={{borderRadius: "6px", mt: 0.75}}/>
            </Box>
          </Stack>
          <Stack direction={"row"} gap={1}>
            <Skeleton variant={"rounded"} width={110} height={34} sx={{borderRadius: "10px"}}/>
            <Skeleton variant={"circular"} width={34} height={34}/>
          </Stack>
        </Stack>
        <Stack gap={2} sx={{px: 3, py: 2}}>
          {[0.6, 0.4, 0.7, 0.5].map((width, index) => (
            <Skeleton key={index} variant={"rounded"} height={48}
                      sx={{width: `${width * 100}%`, borderRadius: "14px", alignSelf: index % 2 ? "flex-end" : "flex-start"}}/>
          ))}
        </Stack>
      </Stack>
    );
  }

  const name = conversation.senderName || flow?.phonebook?.[conversation.sender] || conversation.sender;
  const avatar = AVATAR_COLORS[(Number(conversation.id) || 0) % AVATAR_COLORS.length];
  const language = subProjectOf(conversation);

  return (
    <Stack sx={{height: "100%", minHeight: 0}}>
      <ContactPhoneNumber
        conversation={conversation}
        open={openContact}
        handleClose={() => setOpenContact(false)}
        onSaved={(saved) => setConversation(current => current ? {...current, senderName: saved?.senderName} : current)}
      />

      {/* Header */}
      <Stack direction={{xs: "column", lg: "row"}} justifyContent={"space-between"} alignItems={{lg: "center"}} gap={1.5}
             sx={{position: "relative", pl: {xs: 2, md: 3}, pr: {xs: 2, md: 3, lg: 9}, pt: 2.5, pb: 2, borderBottom: "1px solid #F0F0F6"}}>
        <Tooltip title={"Close"}>
          <IconButton sx={{...roundButtonSx, border: 0, position: "absolute", top: 25, right: {xs: 12, md: 20}}} onClick={close}>
            <HugeiconsIcon icon={Cancel01Icon} size={18}/>
          </IconButton>
        </Tooltip>
        <Stack direction={"row"} alignItems={"center"} gap={1.5} sx={{minWidth: 0, pr: {xs: 5, lg: 0}}}>
          <Box sx={{
            width: 44, height: 44, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 15, fontWeight: 700, bgcolor: avatar.bg, color: avatar.color,
          }}>
            {initialsOf(name) || <HugeiconsIcon icon={CustomerSupportIcon} size={20}/>}
          </Box>
          <Box sx={{minWidth: 0}}>
            <Stack direction={"row"} alignItems={"center"} gap={0.5}>
              <SenderName name={name} sender={conversation.sender} sx={{
                fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em", maxWidth: {xs: 200, lg: 280},
              }}/>
              <TransferNumber conversation={conversation} size={"medium"}/>
              <Tooltip title={conversation.senderName ? "Edit contact" : "Add as contact"}>
                <IconButton size={"small"} onClick={() => setOpenContact(true)} sx={{color: "#8A8AA0"}}>
                  <HugeiconsIcon icon={UserEdit01Icon} size={16}/>
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"} sx={{mt: 0.25}}>
              <StatusBadge conversation={conversation}/>
              <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                #{conversation.id} · {moment(conversation.dateCreated).format("DD MMM YYYY, HH:mm")}
                {language ? ` · ${subProjectLabel(language)}` : ""}
                {isVoice ? ` · ${conversation.monitoredBy ? `Supervised by ${conversation.monitoredBy}` : "Not supervised"}` : ""}
              </Typography>
              <TagPills names={tagsOf(conversation)}/>
            </Stack>
          </Box>
        </Stack>

        <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
          {isVoice && conversation.status === "ACTIVE" && (
            <>
              <Button sx={actionSx()} onClick={monitorConversation} startIcon={<HugeiconsIcon icon={EyeIcon} size={16}/>}>
                {conversation.monitoredBy === currentUser?.username ? "Supervising" : "Supervise"}
              </Button>
              {useBrowserPhone && (
                <SendToGroupButton projectId={project?.id} conversationId={conversation.id} sx={actionSx()}/>
              )}
              <Button sx={actionSx("accent")} onClick={handleHandover}
                      startIcon={<HugeiconsIcon icon={callByBrowserPhone ? HeadsetIcon : PhoneForwardedIcon} size={16}/>}>
                {callByBrowserPhone ? "Take over call" : "Handover"}
              </Button>
            </>
          )}
          {isVoice && conversation.status !== "ACTIVE" && (
            <>
              <Button sx={actionSx()} onClick={toggleResolved}
                      startIcon={<HugeiconsIcon icon={conversation.resolved ? Refresh01Icon : CircleCheckIcon} size={16}/>}>
                {conversation.resolved ? "Mark unresolved" : "Mark resolved"}
              </Button>
              {canHandover && (
                <Button sx={actionSx("dark")} onClick={callCustomer} startIcon={<HugeiconsIcon icon={PhoneIcon} size={16}/>}>
                  Call customer
                </Button>
              )}
            </>
          )}
          <Tooltip title={showDebug ? "Hide debug messages" : "Show debug messages"}>
            <IconButton
              sx={{...roundButtonSx, ...(showDebug ? {color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)"} : {})}}
              onClick={() => setShowDebug(current => {
                localStorage.setItem("showDebug", JSON.stringify(!current));
                return !current;
              })}
            >
              <HugeiconsIcon icon={Bug01Icon} size={16}/>
            </IconButton>
          </Tooltip>
          <FlowProtectionButton/>
        </Stack>
      </Stack>

      {/* Messages and the caller's earlier conversations */}
      <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", position: "relative", bgcolor: "#FAFAFD"}}>
        <SupervisionConversationHistory
          selectedConversation={conversation}
          setSelectedConversation={(next) => {
            const value = typeof next === "function" ? next(conversation) : next;
            if (value?.id && String(value.id) !== String(conversationId)) onOpen(value);
            else setConversation(value);
          }}
          debug={showDebug}
          project={flow}
          conversationType={conversation.type}
        />
      </Box>
    </Stack>
  );
};
